import { Contract, PropositionProductType } from "@dune/types";
import { Badge } from "@dune/ui";
import {
	formatCurrency,
	formatSimpleDate,
	getActionTypesLabelNames,
	getBuildingTypeLabelName,
	getPropositionStatusBadgeClasses,
	getPropositionStatusLabel,
} from "@dune/utils";
import { ColumnDef } from "@tanstack/react-table";

export const contractsColumns = (): ColumnDef<Contract>[] => [
	{
		accessorKey: "reference",
		header: "Ref",
		cell: ({ row }) => (
			<div className="font-medium">{row.getValue("reference")}</div>
		),
	},
	{
		accessorKey: "contractNumber",
		header: "N° Contrat",
		cell: ({ row }) => (
			<div className="font-medium">{row.original.contractNumber || "N/A"}</div>
		),
	},
	{
		accessorKey: "brokerSociety",
		header: "Apporteur(s)",
		cell: ({ row }) => {
			const broker = row.original.brokerSociety;
			return (
				<div>
					<div className="font-medium">{broker?.denomination || "N/A"}</div>
				</div>
			);
		},
	},
	{
		accessorKey: "constructionSite",
		header: "Maître d'ouvrage",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const clientDetails = site?.constructionSiteClientDetails;
			if (!clientDetails) return <div>N/A</div>;

			const showMOA = clientDetails.clientIsMOA;
			let moaName: string | undefined;
			if (showMOA) {
				if (clientDetails.moaClientType === "SOCIETY") {
					moaName = clientDetails.moaClientSociety?.denomination;
				} else if (clientDetails.moaClientType === "USER") {
					const first = clientDetails.moaClientContact?.firstname || "";
					const last = clientDetails.moaClientContact?.lastname || "";
					moaName = `${first} ${last}`.trim();
				}
			}

			return (
				<div>
					<div className="font-medium">
						{showMOA
							? moaName || "Pas de MOA identifié"
							: "Pas de MOA identifié"}
					</div>
					{!showMOA && clientDetails.clientSociety?.denomination && (
						<div className="text-sm text-gray-500">
							{clientDetails.clientSociety.denomination}
						</div>
					)}
				</div>
			);
		},
	},
	{
		accessorKey: "constructionType",
		header: "Type de projet",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const actions = site?.constructionSiteDetails?.constructionSiteAction;
			const primaryAction = actions;
			const actionTypes = primaryAction?.types || [];
			const translatedTypes = getActionTypesLabelNames(actionTypes);
			return (
				<div>
					<div className="font-medium">{translatedTypes || "N/A"}</div>
				</div>
			);
		},
	},
	{
		accessorKey: "constructionSiteType",
		header: "Type de chantier",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const actions = site?.constructionSiteDetails?.constructionSiteAction;
			const primaryAction = actions;
			const buildingType = primaryAction?.buildingType;
			const translatedType = getBuildingTypeLabelName(buildingType || "");
			return (
				<div>
					<div className="font-medium">{translatedType || "N/A"}</div>
				</div>
			);
		},
	},
	{
		accessorKey: "totalCost",
		header: "Coût",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const details = site?.constructionSiteDetails;
			return (
				<div className="font-medium">
					{details?.totalAmountTTC
						? formatCurrency(details.totalAmountTTC)
						: "N/A"}
				</div>
			);
		},
	},
	{
		accessorKey: "address",
		header: "Adresse",
		cell: ({ row }) => {
			const site =
				row.original.constructionSite || row.original.gfaConstructionSite;
			const address = site?.constructionSiteDetails?.address;
			return (
				<div>
					<div className="font-medium">
						{address?.streetNumber || ""} {address?.streetName || "N/A"}
					</div>
					<div className="text-sm text-gray-500">
						{address?.city || ""} {address?.postalCode || ""}
					</div>
				</div>
			);
		},
	},
	{
		accessorKey: "signedAt",
		header: "Date signature",
		cell: ({ row }) => (
			<div className="text-sm">
				{row.original.signedAt
					? formatSimpleDate(row.original.signedAt)
					: "N/A"}
			</div>
		),
	},
	{
		accessorKey: "contractStartDate",
		header: "Début contrat",
		cell: ({ row }) => (
			<div className="text-sm">
				{row.original.contractStartDate
					? formatSimpleDate(row.original.contractStartDate)
					: "N/A"}
			</div>
		),
	},
	{
		accessorKey: "contractEndDate",
		header: "Fin contrat",
		cell: ({ row }) => (
			<div className="text-sm">
				{row.original.contractEndDate
					? formatSimpleDate(row.original.contractEndDate)
					: "N/A"}
			</div>
		),
	},
	{
		accessorKey: "status",
		header: "Statut",
		cell: ({ row }) => {
			const status = row.original.status;
			const classes = getPropositionStatusBadgeClasses(status);
			const label = getPropositionStatusLabel(status);
			return (
				<div
					className={`inline-flex items-center px-2 py-1 rounded-full text-xs border whitespace-nowrap ${classes}`}
				>
					{label}
				</div>
			);
		},
	},
	{
		accessorKey: "productType",
		header: "Produit",
		cell: ({ row }) => {
			const type = row.original.productType;
			const typeLabels = {
				[PropositionProductType.DO]: "DO",
				[PropositionProductType.TRC]: "TRC",
				[PropositionProductType.MRT]: "MRT",
				[PropositionProductType.GFA]: "GFA",
			};
			return (
				<Badge variant="outline">
					{typeLabels[type as keyof typeof typeLabels] || type}
				</Badge>
			);
		},
	},
	{
		accessorKey: "primeTTC",
		header: "Prime TTC",
		cell: ({ row }) => (
			<div className="font-medium">
				{formatCurrency(row.getValue("primeTTC"))}
			</div>
		),
	},
	{
		accessorKey: "assureurName",
		header: "Assureur",
		cell: ({ row }) => (
			<div className="font-medium">{row.getValue("assureurName") || "N/A"}</div>
		),
	},
	// actions column removed
];
